import React, { FC, useCallback, useState } from 'react';

import { useRouter } from 'expo-router';
import { View } from 'react-native';

import { centeredRowContainerStyles } from 'components/layout/centered-row-container';
import { PullToReloadScrollView } from 'components/pull-to-reload/pull-to-reload';
import { QuestSection } from 'components/quests';
import { LoyaltyNotEnrolled } from 'pages/loyalty/loyalty-not-enrolled';
import { LoyaltyRewardsExpiringPoints } from 'pages/loyalty/loyalty-rewards/loyalty-rewards-expiring-points';
import { LoyaltyRewardsList } from 'pages/loyalty/loyalty-rewards/loyalty-rewards-list';
import { LoyaltyRewardsSkeletonLoader } from 'pages/loyalty/loyalty-rewards/loyalty-rewards-skeleton-loader';
import { LoyaltyRewardsListContainer } from 'pages/loyalty/loyalty-rewards/loyalty-rewards.styled';
import { CustomEventNames, logLoyaltyEvent } from 'state/amplitude';
import { useAuthContext } from 'state/auth';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLoyaltyContext } from 'state/loyalty';
import { AllowedCelebrationTypes } from 'state/loyalty/hooks/types';
import { useLoyaltyCelebrations } from 'state/loyalty/hooks/use-loyalty-celebrations';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { useLoyaltyUserState } from 'state/loyalty/hooks/use-loyalty-user-state';
import { LoyaltyReward } from 'state/loyalty/types';
import { startTrace } from 'utils/performance/trace';
import { TraceName } from 'utils/performance/trace';
import { routes } from 'utils/routing';
import { waitForDuration } from 'utils/wait-for-duration';

import { useLoyaltyServiceRewards } from '../hooks/use-loyalty-service-rewards';

export const RewardsBase: FC = () => {
  const { user, loading: userDataIsLoading, isAuthenticated } = useAuthContext();
  const { refetchLoyaltyUser } = useLoyaltyContext();
  const { loading: loyaltyUserLoading, loyaltyUser } = useLoyaltyUser();

  const {
    rewardCategories,
    sanityRewardsMap,
    engineRewardsMap,
    rewardsEvaluationMap,
    loading: rewardDataIsLoading,
    refreshRewards,
  } = useLoyaltyServiceRewards();

  const enableLoyaltyCelebrations = useFlag(LaunchDarklyFlag.ENABLE_LOYALTY_CELEBRATIONS);

  const { recheckCelebrations } = useLoyaltyCelebrations({
    allowedCelebrationTypes: [
      AllowedCelebrationTypes.CHALLENGE_COMPLETED,
      AllowedCelebrationTypes.COMBINED_CHALLENGE_COMPLETED,
      AllowedCelebrationTypes.PROFILE_CHALLENGE_COMPLETED,
    ],
    disableCelebrations: !enableLoyaltyCelebrations,
  });

  const [isForceRefreshing, setForceRefresh] = useState<boolean>(false);

  const router = useRouter();

  const loyaltyId = user?.loyaltyId;

  // Refetch for updated crowns when navigating to page
  useLoyaltyUserState({ refreshOnMountOrFocus: true });

  const handleRewardOnPress = useCallback(
    (reward: LoyaltyReward) => {
      startTrace(TraceName.REWARD_DETAIL_LOAD, { id: reward._id, name: reward.name?.locale || '' });
      const rewardId = reward._id;
      logLoyaltyEvent({
        name: CustomEventNames.REWARD_SELECTED,
        incentives: [reward],
      });

      router.navigate(`${routes.loyaltyRewardsList}/${rewardId}`);
    },
    [router]
  );

  const forceRefresh = async () => {
    await waitForDuration(1500);
    if (loyaltyId) {
      refetchLoyaltyUser();
      setForceRefresh(true);
      await refreshRewards();
      setForceRefresh(false);
      recheckCelebrations();
    }
  };

  const retrievingUser = !loyaltyUser && loyaltyUserLoading;

  if (userDataIsLoading || retrievingUser || rewardDataIsLoading || isForceRefreshing) {
    return <LoyaltyRewardsSkeletonLoader />;
  }

  if (isAuthenticated && !loyaltyId) {
    return <LoyaltyNotEnrolled />;
  }

  return (
    <PullToReloadScrollView
      progressViewOffset={10}
      onRefresh={forceRefresh}
      contentContainerStyle={centeredRowContainerStyles.container}
      isDisabled={!isAuthenticated}
    >
      <View style={centeredRowContainerStyles.content}>
        <LoyaltyRewardsExpiringPoints />
        <QuestSection showHeader={false} />
        <LoyaltyRewardsListContainer>
          <LoyaltyRewardsList
            handleOnPress={handleRewardOnPress}
            rewardCategories={rewardCategories}
            engineRewardsMap={engineRewardsMap}
            rewardsEvaluationMap={rewardsEvaluationMap}
            sanityRewardsMap={sanityRewardsMap}
          />
        </LoyaltyRewardsListContainer>
      </View>
    </PullToReloadScrollView>
  );
};
